import { PauseCover } from "aic";
import { RouteObject } from "react-router-dom";
import AppSettingsContextProvider from "./contexts/AppSettingsContextProvider";
import { hierarchyLegacyTagRedirectLoader } from "./functions/api/hierarchy/hierarchyLegacyTagRedirectLoader";
import { hierarchyLoader } from "./functions/api/hierarchy/hierarchyLoader";
import { hierarchyRedirectLoader } from "./functions/api/hierarchy/hierarchyRedirectLoader";
import { newTlfLoader } from "./functions/api/tlfItem/newTlfLoader";
import { tlfLoader, tlfOnlyLoader } from "./functions/api/tlfItem/tlfLoader";
import { tlfRedirectLoader } from "./functions/api/tlfItem/tlfRedirectLoader";
import { appSettingsLoader } from "./functions/api/user/appSettingsLoader";
import RedirectingPage from "./pages/RedirectingPage";
import RouterErrorBoundary from "./pages/RouterErrorBoundary";
import StartPage from "./pages/StartPage";
import TlfReview, { TlfReviewRHS } from "./pages/TlfReview";

/**
 * Determines whether a route loader should be revalidated.
 * When a navigation occurs (currentUrl differs from nextUrl), revalidation is
 * only triggered if the pathname changes, preventing unnecessary loader runs
 * when only search parameters are modified. For non-navigation triggers such
 * as explicit revalidation or action-driven refreshes (where both URLs are
 * identical), the router's default decision is used.
 *
 * @param args - Revalidation arguments from React Router.
 * @param args.currentUrl - URL for the current route state before navigation.
 * @param args.nextUrl - URL for the target route state after navigation.
 * @param args.defaultShouldRevalidate - The router's default revalidation decision,
 *        used as a fallback for non-navigation triggers.
 * @returns True when the pathname changes, or when the router would revalidate by default.
 */
const shouldRevalidateOnPathnameChange = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: {
  currentUrl: URL;
  nextUrl: URL;
  defaultShouldRevalidate: boolean;
}): boolean => {
  return currentUrl.href === nextUrl.href
    ? defaultShouldRevalidate
    : currentUrl.pathname !== nextUrl.pathname;
};

export const routes: RouteObject[] = [
  {
    path: "/",
    loader: appSettingsLoader,
    errorElement: <RouterErrorBoundary />,
    element: <AppSettingsContextProvider />,
    hydrateFallbackElement: <PauseCover text="Loading application..." />,
    children: [
      {
        index: true,
        element: <StartPage />,
      },
      // Admin page
      {
        path: "/Admin",
        lazy: async () => ({
          Component: (await import("./pages/AdminPage")).AdminPage,
        }),
      },
      // Maintenance page
      {
        path: "/Maintenance",
        lazy: async () => ({
          Component: (await import("./pages/MaintenancePage")).MaintenancePage,
        }),
      },
      // Not Allowed Page
      {
        path: "/NotAllowed",
        lazy: async () => ({
          Component: (await import("./pages/NotAllowedPage")).NotAllowedPage,
        }),
      },

      // Legacy tag-based hierarchy redirects — preserve backward compatibility
      // for bookmarks and external links that used the old level-prefixed URL scheme,
      // including deep links (for example: /TA/:hierarchyTag/Access).
      ...[
        "/TA/:hierarchyTag",
        "/TA/:hierarchyTag/:more",
        "/TA/:hierarchyTag/:more/*",
        "/Project/:hierarchyTag",
        "/Project/:hierarchyTag/:more",
        "/Project/:hierarchyTag/:more/*",
        "/Study/:hierarchyTag",
        "/Study/:hierarchyTag/:more",
        "/Study/:hierarchyTag/:more/*",
        "/Study/:studyName/Report/:reportName",
        "/Study/:studyName/Report/:reportName/:more",
        "/Study/:studyName/Report/:reportName/:more/*",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion/:more",
        "/Study/:studyName/Report/:reportName/Version/:tocMajorVersion/:more/*",
      ].map((path) => ({
        path,
        loader: hierarchyLegacyTagRedirectLoader,
        element: <RedirectingPage />,
      })),

      // Ancestor tags encoded in path segments so the
      // backend can resolve the hierarchy within the correct parent scope.
      ...[
        "/Corporate",
        "/:taPrefix",
        "/:taPrefix/:projectCode",
        "/:taPrefix/:projectCode/:studyTag",
        "/:taPrefix/:projectCode/:studyTag/:reportName",
        "/:taPrefix/:projectCode/:studyTag/:reportName/Version/:tocMajorVersion",
      ].map((path) => ({
        path,
        loader: hierarchyLoader,
        lazy: async () => ({
          Component: (await import("./pages/HierarchyPage")).HierarchyPage,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: path.includes(":reportName")
                ? (await import("./components/toc/TOC")).TOC
                : (await import("./components/hierarchypage/HierarchyProperties"))
                    .HierarchyProperties,
            }),
          },
          {
            path: "Access",
            lazy: async () => ({
              Component: (await import("./components/access/AccessRights")).AccessRights,
            }),
          },
          {
            path: "ClinicalLibrary",
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryNavigation"))
                    .LibraryNavigation,
                }),
              },
              {
                path: "CodeLists",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "CodeList/:codeListName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "DataSets",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "DataSet/:dataSetName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "ValueLists",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "ValueList/:valueListName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Comments",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Methods",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClauses",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClause/:whereClauseName/RangeCheck/:rangeCheckId",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "WhereClause/:whereClauseName",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Comment/:commentId",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
              {
                path: "Method/:methodOid",
                lazy: async () => ({
                  Component: (await import("./components/library/LibraryTable")).LibraryTable,
                }),
              },
            ],
          },
          {
            path: "DomainReplacements",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyDomainReplacements"))
                .HierarchyDomainReplacements,
            }),
          },
          {
            path: "Lists",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyLists")).HierarchyLists,
            }),
          },
          {
            path: "Properties",
            lazy: async () => ({
              Component: (await import("./components/hierarchypage/HierarchyProperties"))
                .HierarchyProperties,
            }),
          },
          {
            path: "Replacements",
            lazy: async () => ({
              Component: (await import("./components/inherited/ExternalSinglesTable"))
                .ExternalSinglesTable,
            }),
          },
          {
            path: "RowGroups",
            lazy: async () => ({
              Component: (await import("./components/inherited/HierarchyRowGroups"))
                .HierarchyRowGroups,
            }),
          },
          {
            path: "AnalysisSets",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyAnalysisSets"))
                .HierarchyAnalysisSets,
            }),
          },
          {
            path: "DataSubsets",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyDataSubsets"))
                .HierarchyDataSubsets,
            }),
          },
          {
            path: "GroupingFactors",
            lazy: async () => ({
              Component: (await import("./components/ars/HierarchyGroupingFactors"))
                .HierarchyGroupingFactors,
            }),
          },
          {
            path: "TOC",
            lazy: async () => ({
              Component: path.includes(":reportName")
                ? (await import("./components/toc/TOC")).TOC
                : (await import("./pages/NotFound")).NotFound,
            }),
          },
          {
            path: "Versions",
            lazy: async () => ({
              Component: path.includes(":reportName")
                ? (await import("./components/toc/ReportVersions")).ReportVersions
                : (await import("./components/hierarchypage/HierarchyHistoryTable"))
                    .HierarchyHistoryTable,
            }),
          },
          {
            path: "AdvancedSearch",
            lazy: async () => ({
              Component: (
                await import("./components/common/editorBlocksSearch/EditorBlocksSearchControls")
              ).EditorBlocksSearchControls,
            }),
          },
        ],
      })),

      // TLF pages (with hierarchy ancestor tags)
      ...[
        "/Corporate",
        "/:taPrefix",
        "/:taPrefix/:projectCode",
        "/:taPrefix/:projectCode/:studyTag",
        "/:taPrefix/:projectCode/:studyTag/:reportName",
      ].map((path) => ({
        path: `${path}/NewTlf/:newType`,
        loader: newTlfLoader,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Old redirect links (numeric / UUID IDs)
      ...[
        "/H/:hierarchyId",
        "/H/:hierarchyId/:more",
        "/S/:hierarchyId",
        "/S/:hierarchyId/:more",
        "/R/:hierarchyId",
        "/R/:hierarchyId/:more",
      ].map((path) => ({
        path,
        loader: hierarchyRedirectLoader,
        element: <RedirectingPage />,
      })),

      // Old TlfDesigner pages and redirect from search
      ...["/TlfDesigner/:azsolId/:version", "/TlfDesigner/:id"].map((path) => ({
        path,
        loader: tlfRedirectLoader,
        element: <RedirectingPage />,
      })),

      // Load tlf (with hierarchy ancestor tags)
      ...[
        "/Corporate/Load",
        "/:taPrefix/Load",
        "/:taPrefix/:projectCode/Load",
        "/:taPrefix/:projectCode/:studyTag/Load",
        "/:taPrefix/:projectCode/:studyTag/:reportName/Load",
        "/:taPrefix/:projectCode/:studyTag/:reportName/Version/:tocMajorVersion/Load",
      ].map((path) => ({
        path,
        loader: tlfLoader,
        shouldRevalidate: shouldRevalidateOnPathnameChange,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Template pages — a static "Template" segment is required for all levels to avoid
      // ambiguity with the all-dynamic hierarchy routes. Without it,
      // /:taPrefix/:azsolId/:version is indistinguishable from
      // /:taPrefix/:projectCode/:studyTag (Study hierarchy), and
      // /:taPrefix/:projectCode/:azsolId/:version is indistinguishable from
      // /:taPrefix/:projectCode/:studyTag/:reportName (Report hierarchy).
      // Corporate could technically omit the prefix (the static "Corporate" segment
      // already disambiguates), but the prefix is kept for consistency.
      ...[
        "/Corporate/Template/:azsolId/:version",
        "/:taPrefix/Template/:azsolId/:version",
        "/:taPrefix/:projectCode/Template/:azsolId/:version",
      ].map((path) => ({
        path,
        loader: tlfLoader,
        shouldRevalidate: shouldRevalidateOnPathnameChange,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Shell pages (with hierarchy ancestor tag)
      ...[
        "/:taPrefix/:projectCode/:studyTag/:reportName/:tocNumber",
        "/:taPrefix/:projectCode/:studyTag/:reportName/Version/:tocMajorVersion/:tocNumber",
      ].map((path) => ({
        path,
        loader: tlfLoader,
        shouldRevalidate: shouldRevalidateOnPathnameChange,
        lazy: async () => ({
          Component: (await import("./pages/TlfPage")).TlfPage,
        }),
      })),

      // Review pages (with hierarchy ancestor tags)
      ...["/:taPrefix/:projectCode/:studyTag/:reportName/Review"].map((path) => ({
        path,
        loader: hierarchyLoader,
        element: <TlfReview />,
        children: [
          { path: ":tocNumber", loader: tlfOnlyLoader, element: <TlfReviewRHS /> },
          {
            index: true,
            element: (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                Choose from the list of TLF items
              </div>
            ),
          },
        ],
      })),

      // Not found splat
      {
        path: "/*",
        lazy: async () => ({
          Component: (await import("./pages/NotFound")).NotFound,
        }),
      },
    ],
  },
];
